import type {
  MerchantAuditLogListParams,
  MerchantAuditLogListResponse,
  MerchantAuditLogEntry,
} from '../types';
import type { RequestFn } from '../client';

/**
 * A merchant's own audit log — everything changed on their account and their
 * shops, grouped by the sign-in it happened in.
 *
 * Requires a dashboard JWT (`setJwtToken`) whose role holds the `AuditLog`
 * permission; an API key cannot read it. This is deliberate: the log names
 * people, and an API key names no one.
 *
 * There is no `merchant_id` parameter. The scope comes from the token, so this
 * resource cannot be pointed at another merchant — see
 * {@link MerchantAuditLogListParams}.
 */
export class Audit {
  constructor(private readonly request: RequestFn) {}

  async list(params?: MerchantAuditLogListParams): Promise<MerchantAuditLogListResponse> {
    return this.request('GET', '/audit', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * One entry. An id belonging to another merchant answers 404, not 403 — the
   * endpoint does not confirm that an entry it will not serve exists.
   */
  async retrieve(logId: string): Promise<MerchantAuditLogEntry> {
    return this.request('GET', `/audit/${encodeURIComponent(logId)}`);
  }
}
